import { IActions } from './IActions';
import { AxiosInstance, CancelToken } from 'axios';
import { State } from './State';
import { Config } from './Config';
import { IPagedResult } from './IPagedResult';
import FilterBuilder, { QueryBuilder } from 'odata-query-builder';
import { Filter } from './Filter';

export class OdataApiActions implements IActions {
  private axios: AxiosInstance;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private key: (entity: any) => string | number;
  private baseUrl: string;
  private customConfigCallback?: (queryBuilder: QueryBuilder) => QueryBuilder | undefined;

  constructor(
    axios: AxiosInstance,
    apiRoute: string,
    entityName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    key: (entity: any) => string | number,
    customConfigCallback?: (queryBuilder: QueryBuilder) => QueryBuilder | undefined
  ) {
    this.axios = axios;
    this.baseUrl = apiRoute + entityName;
    this.key = key;
    this.customConfigCallback = customConfigCallback;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  public add = (entity: any) =>
    this.axios.post(this.baseUrl, entity).then((response) => response.data);

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  public update = (entity: any) =>
    this.axios.put(`${this.baseUrl}/${this.key(entity)}`, entity).then((response) => response.data);

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  public delete = (entity: any) =>
    this.axios
      .delete(`${this.baseUrl}/${this.key(entity)}`, { data: entity })
      .then((response) => response.data);

  public stateChanged = (
    skip: number,
    take: number,
    state: State,
    config: Config,
    search: string,
    cancelToken: CancelToken
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<IPagedResult<any>> => {
    const allFilters = config.permanentFilters.concat(state.filters);

    this.fixFilterValueTypes(allFilters);

    const queryBuilder: QueryBuilder = new QueryBuilder()
      .top(take)
      .skip(skip)
      .count()
      .orderBy(
        state.sorting
          .map((s) => `${s.sortBy} ${s.sortMode === 'descending' ? 'desc' : 'asc'}`)
          .join(',')
      );

    if (allFilters.length > 0) {
      this.applyFilters(queryBuilder, allFilters);
    }

    if (this.customConfigCallback !== undefined && this.customConfigCallback !== null) {
      this.customConfigCallback(queryBuilder);
    }

    // @ts-ignore
    return this.axios
      .get(`${this.baseUrl}${queryBuilder.toQuery()}`, { cancelToken: cancelToken })
      .then((response) => {
        if (!response) {
          return undefined;
        }

        return {
          entities: response.data.value,
          skipped: skip,
          taken: take,
          totalCount: response.data['@odata.count']
        };
      })
      .catch((error: Error) => {
        // @ts-ignore
        if (error.__CANCEL__ === false) {
          throw error;
        }
      });
  };

  private fixFilterValueTypes(allFilters: Filter[]) {
    for (const filter of allFilters) {
      if (filter.fieldType === 'numeric') {
        for (let valueIndex = 0; valueIndex < filter.filterQuery.length; valueIndex++) {
          filter.filterQuery[valueIndex] = +filter.filterQuery[valueIndex];
        }
      }
    }
  }

  private applyFilters(queryBuilder: QueryBuilder, allFilters: Filter[]) {
    queryBuilder.filter((fb) => {
      fb.and((fbAnd) => {
        this.handleContains(allFilters, fbAnd);
        this.handleStartsWith(allFilters, fbAnd);
        this.handleEquals(allFilters, fbAnd);
        this.handleNotEquals(allFilters, fbAnd);
        this.handleBefore(allFilters, fbAnd);
        this.handleAfter(allFilters, fbAnd);
        this.handleBetween(allFilters, fbAnd);
        return fbAnd;
      });
      return fb;
    });
  }

  private cleanedFieldName(fieldName: string): string {
    return fieldName.replaceAll('__', '/');
  }

  private handleContains(allFilters: Filter[], fbAnd: FilterBuilder) {
    const containsFilters = allFilters.filter((f) => f.filterType === 'contains');
    containsFilters.forEach((containsFilter) => {
      fbAnd.or((fbOr) => {
        containsFilter.filterQuery.forEach((filterValue) =>
          fbOr.filterPhrase(
            `contains(cast(${this.cleanedFieldName(
              containsFilter.fieldName
            )}, 'Edm.String'),'${filterValue}')`
          )
        );
        return fbOr;
      });
    });
  }

  private handleStartsWith(allFilters: Filter[], fbAnd: FilterBuilder) {
    const startsWithFilters = allFilters.filter((f) => f.filterType === 'startsWith');
    startsWithFilters.forEach((startsWithFilter) => {
      fbAnd.or((fbOr) => {
        startsWithFilter.filterQuery.forEach((filterValue) =>
          fbOr.filterPhrase(
            `startswith(cast(${this.cleanedFieldName(
              startsWithFilter.fieldName
            )}, 'Edm.String'),'${filterValue}')`
          )
        );
        return fbOr;
      });
    });
  }

  private handleEquals(allFilters: Filter[], fbAnd: FilterBuilder) {
    const equalsFilters = allFilters.filter((f) => f.filterType === 'equals');
    equalsFilters.forEach((equalsFilter) => {
      if (equalsFilter.fieldType === 'boolean') {
        const filterValue = equalsFilter.filterQuery.indexOf(true) >= 0;
        fbAnd.filterExpression(this.cleanedFieldName(equalsFilter.fieldName), 'eq', filterValue);
      } else if (equalsFilter.fieldType === 'date') {
        fbAnd.or((fbOr) => {
          equalsFilter.filterQuery.forEach((filterValue) => {
            if (filterValue === '') {
              return fbOr.filterPhrase(`${this.cleanedFieldName(equalsFilter.fieldName)} eq null`);
            } else {
              return fbOr.filterExpression(
                this.cleanedFieldName(equalsFilter.fieldName),
                'eq',
                filterValue
              );
            }
          });
          return fbOr;
        });
      } else {
        fbAnd.or((fbOr) => {
          equalsFilter.filterQuery.forEach((filterValue) =>
            fbOr.filterExpression(this.cleanedFieldName(equalsFilter.fieldName), 'eq', filterValue)
          );
          return fbOr;
        });
      }
    });
  }

  private handleNotEquals(allFilters: Filter[], fbAnd: FilterBuilder) {
    const notEqualsFilters = allFilters.filter((f) => f.filterType === 'notequals');
    notEqualsFilters.forEach((notEqualsFilter) => {
      if (notEqualsFilter.fieldType === 'boolean') {
        const filterValue = notEqualsFilter.filterQuery.indexOf(true) >= 0;
        fbAnd.filterExpression(this.cleanedFieldName(notEqualsFilter.fieldName), 'ne', filterValue);
      } else if (notEqualsFilter.fieldType === 'date') {
        fbAnd.or((fbOr) => {
          notEqualsFilter.filterQuery.forEach((filterValue) => {
            if (filterValue === '') {
              return fbOr.filterPhrase(
                `${this.cleanedFieldName(notEqualsFilter.fieldName)} ne null`
              );
            } else {
              return fbOr.filterExpression(
                this.cleanedFieldName(notEqualsFilter.fieldName),
                'ne',
                filterValue
              );
            }
          });
          return fbOr;
        });
      } else {
        fbAnd.or((fbOr) => {
          notEqualsFilter.filterQuery.forEach((filterValue) =>
            fbOr.filterExpression(
              this.cleanedFieldName(notEqualsFilter.fieldName),
              'ne',
              filterValue
            )
          );
          return fbOr;
        });
      }
    });
  }

  private handleBefore(allFilters: Filter[], fbAnd: FilterBuilder) {
    const beforeFilters = allFilters.filter((f) => f.filterType === 'before');
    beforeFilters.forEach((beforeFilter) => {
      fbAnd.or((fbOr) => {
        beforeFilter.filterQuery.forEach((filterValue) =>
          fbOr.filterPhrase(`${this.cleanedFieldName(beforeFilter.fieldName)} le ${filterValue}`)
        );
        return fbOr;
      });
    });
  }

  private handleAfter(allFilters: Filter[], fbAnd: FilterBuilder) {
    const afterFilters = allFilters.filter((f) => f.filterType === 'after');
    afterFilters.forEach((afterFilter) => {
      fbAnd.or((fbOr) => {
        afterFilter.filterQuery.forEach((filterValue) =>
          fbOr.filterPhrase(`${this.cleanedFieldName(afterFilter.fieldName)} ge ${filterValue}`)
        );
        return fbOr;
      });
    });
  }

  private handleBetween(allFilters: Filter[], fbAnd: FilterBuilder) {
    const betweenFilters = allFilters.filter((f) => f.filterType === 'between');
    betweenFilters.forEach((betweenFilter) => {
      fbAnd.or((fbOr) => {
        betweenFilter.filterQuery.forEach((filterValue) => {
          const filterValues = ('' + filterValue).split(',');
          return fbOr.filterPhrase(
            `(${this.cleanedFieldName(betweenFilter.fieldName)} ge ${
              filterValues[0]
            } and ${this.cleanedFieldName(betweenFilter.fieldName)} le ${filterValues[1]})`
          );
        });
        return fbOr;
      });
    });
  }

  public load = this.stateChanged;
}
